import { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom';

import { isUsingProductionBuild } from '@sureapp/wl-toggle-common/dist/services/isEnvironment';
import { initSentry } from '@sureapp/wl-toggle-common/dist/services/sentry';
import analytics from '@sureapp/wl-toggle-common/dist/utils/analytics';
import { Route, Switch, BrowserRouter as Router } from 'react-router-dom';

import SpinnerView from '@@/auto/components/SpinnerView';
import type { IWindow } from '@@/auto/interfaces/window.interface';
import { captureException, isSentryEnabled } from '@@/auto/utils/sentry';

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window extends IWindow {}
}

const App = lazy(() => import('./components/App'));

/*
 * Configure third-party
 */
if (isUsingProductionBuild()) {
  if (isSentryEnabled()) {
    const homeownersPrefix = '/homeowners';
    const isProduction = process.env.REACT_APP_ENV === 'production';

    initSentry({
      dsn: process.env.REACT_APP_SENTRY_DSN!,
      env: process.env.REACT_APP_ENV!,
      release: process.env.REACT_APP_COMMIT_SHA!,
      beforeSend(event) {
        return event.request?.url?.includes(homeownersPrefix) ? null : event;
      },
      beforeBreadcrumb(breadcrumb) {
        return window.location.pathname.startsWith(homeownersPrefix)
          ? null
          : breadcrumb;
      },
      expectedAppErrorMessages: [
        'Could not authenticate with Okta',
        'One or more vehicles were unable to be rated.',
        'The household was unable to be rated.',
      ],
      sessionReplayConfigOverrides: {
        maskAllText: false, // *** If there is PII displayed in an HTML element (<p>, <h1>, <div>, etc) we will need to manually mask ***,
        maskAllInputs: isProduction,
        blockAllMedia: isProduction,
        networkDetailAllowUrls: [
          'https://api.soda14.com',
          'https://api.trygettoggle.com',
          'https://api.gettoggle.com',
        ],
        networkCaptureBodies: !isProduction,
        networkResponseHeaders: ['X-Request-Id'],
      },
    });
  }

  if (process.env.REACT_APP_SEGMENT_WRITE_KEY) {
    analytics.load(process.env.REACT_APP_SEGMENT_WRITE_KEY);
  }

  setTimeout(() => {
    const s = document.createElement('script');
    s.id = 'ze-snippet';
    s.src = `https://static.zdassets.com/ekr/snippet.js?key=${process.env.REACT_APP_ZENDESK_KEY}`;
    s.onerror = (err) =>
      captureException({ exception: err as string, context: {} });
    document.head.appendChild(s);
  }, 2500);
}

/**
 * Mount the app in standalone mode
 */
if (process.env.REACT_APP_STANDALONE === 'true') {
  ReactDOM.render(
    <Router>
      <Suspense fallback={<SpinnerView data-testid="App_spinner" />}>
        <Switch>
          <Route component={App} />
        </Switch>
      </Suspense>
    </Router>,
    document.getElementById('root')
  );
}

export default function Root() {
  return (
    <Router>
      <Suspense fallback={<SpinnerView data-testid="App_spinner" />}>
        <Switch>
          <Route component={App} />
        </Switch>
      </Suspense>
    </Router>
  );
}
